import type { PractitionerRole, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import {
  ROLE_TRANSITION_TASK_CODE,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { collapseRolesForDisplay } from '@unify/fhir-model/role-display';
import { getPractitionerRoleCodes, getRoleAssignedBy } from '@unify/fhir-model/resources/practitioner-role';

export interface RoleAssignment {
  /** Instant the displayed role most recently started showing in Assigned roles. */
  assignedAt?: string;
  /** Display name of the Practitioner who assigned the role. */
  assignedByDisplay?: string;
  /** `Practitioner/{id}` of that actor. */
  assignedByReference?: string;
}

const TASK_REQUESTER_MATCH_MS = 5 * 60 * 1000;

function codesInVersion(pr: PractitionerRole): Set<string> {
  return new Set(collapseRolesForDisplay(getPractitionerRoleCodes(pr)));
}

export function isBotActor(reference?: string, display?: string): boolean {
  if (reference?.startsWith('Bot/')) return true;
  const label = display?.trim().toLowerCase() ?? '';
  return label.includes('admin actions') || label.includes('role assignment');
}

function humanActor(
  reference?: string,
  display?: string,
): { assignedByDisplay?: string; assignedByReference?: string } {
  if (!reference || isBotActor(reference, display)) return {};
  if (!reference.startsWith('Practitioner/')) return {};
  return { assignedByReference: reference, assignedByDisplay: display };
}

function actorFromVersion(pr: PractitionerRole): {
  assignedByDisplay?: string;
  assignedByReference?: string;
} {
  const stamped = getRoleAssignedBy(pr);
  const fromExt = humanActor(stamped?.reference, stamped?.display);
  if (fromExt.assignedByReference) return fromExt;
  return humanActor(pr.meta?.author?.reference, pr.meta?.author?.display);
}

/**
 * Walk PractitionerRole versions oldest → newest. Assigned dates use displayed
 * roles (supervisor subsumes provider) so Change to Provider stamps the demotion.
 */
export function deriveRoleAssignmentsFromHistory(
  versions: PractitionerRole[],
): Record<string, RoleAssignment> {
  const ordered = [...versions].sort((a, b) =>
    (a.meta?.lastUpdated ?? '').localeCompare(b.meta?.lastUpdated ?? ''),
  );

  const assignments: Record<string, RoleAssignment> = {};
  let previous = new Set<string>();

  for (const version of ordered) {
    const current = codesInVersion(version);
    for (const code of current) {
      if (!previous.has(code)) {
        assignments[code] = {
          assignedAt: version.meta?.lastUpdated,
          ...actorFromVersion(version),
        };
      }
    }
    for (const code of previous) {
      if (!current.has(code)) delete assignments[code];
    }
    previous = current;
  }

  return assignments;
}

/** Fill bot-authored history using role-transition Task.requester when timestamps align. */
export function applyRoleTransitionTaskRequesters(
  assignments: Record<string, RoleAssignment>,
  tasks: Task[],
): Record<string, RoleAssignment> {
  const withRequester = tasks.filter((t) => t.requester?.reference?.startsWith('Practitioner/'));
  if (withRequester.length === 0) return assignments;

  const next = { ...assignments };
  for (const [code, assignment] of Object.entries(next)) {
    if (assignment.assignedByReference || !assignment.assignedAt) continue;
    const assignedMs = Date.parse(assignment.assignedAt);
    if (Number.isNaN(assignedMs)) continue;
    let best: Task | undefined;
    let bestDelta = TASK_REQUESTER_MATCH_MS + 1;
    for (const task of withRequester) {
      const taskMs = Date.parse(task.meta?.lastUpdated ?? task.authoredOn ?? '');
      if (Number.isNaN(taskMs)) continue;
      const delta = Math.abs(taskMs - assignedMs);
      if (delta < bestDelta) {
        best = task;
        bestDelta = delta;
      }
    }
    if (!best || bestDelta > TASK_REQUESTER_MATCH_MS) continue;
    next[code] = {
      ...assignment,
      assignedByReference: best.requester?.reference,
      assignedByDisplay: best.requester?.display,
    };
  }
  return next;
}

/**
 * Assignment provenance for a practitioner's active org roles, derived from
 * PractitionerRole version history (no extra resources are written).
 */
export function useRoleAssignments(
  practitionerId: string | undefined,
  refreshToken = 0,
): {
  assignments: Record<string, RoleAssignment>;
  loading: boolean;
} {
  const medplum = useMedplum();
  const [assignments, setAssignments] = useState<Record<string, RoleAssignment>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!practitionerId) {
      setAssignments({});
      return;
    }

    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const roles = await medplum.searchResources('PractitionerRole', {
          practitioner: `Practitioner/${practitionerId}`,
          _count: '20',
        });
        const active = roles.filter((pr) => pr.active !== false);
        const [histories, transitionTasks] = await Promise.all([
          Promise.all(
            active.map(async (pr) => {
              if (!pr.id) return [pr];
              try {
                const bundle = await medplum.readHistory('PractitionerRole', pr.id);
                const versions: PractitionerRole[] = (bundle.entry ?? [])
                  .map((entry) => entry.resource)
                  .filter((r) => r?.resourceType === 'PractitionerRole') as PractitionerRole[];
                return versions.length > 0 ? versions : [pr];
              } catch {
                return [pr];
              }
            }),
          ),
          medplum
            .searchResources('Task', {
              focus: `Practitioner/${practitionerId}`,
              code: `${TASK_TYPE_SYSTEM}|${ROLE_TRANSITION_TASK_CODE}`,
              _count: '20',
              _sort: '-_lastUpdated',
            })
            .catch(() => [] as Task[]),
        ]);
        if (cancelled) return;
        const merged: Record<string, RoleAssignment> = {};
        for (const versions of histories) {
          Object.assign(merged, deriveRoleAssignmentsFromHistory(versions));
        }
        setAssignments(applyRoleTransitionTaskRequesters(merged, transitionTasks));
      } catch {
        if (!cancelled) setAssignments({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, practitionerId, refreshToken]);

  return { assignments, loading };
}
